import type { PatientRecord } from '@/shared/utils/bedboard';

const draftPrefix = 'blink-hoh:comment-draft:v1:';

function draftStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function userPrefix(userID: string) {
  return `${draftPrefix}${encodeURIComponent(userID)}:`;
}

function draftKey(userID: string, targetID: string) {
  return `${userPrefix(userID)}${encodeURIComponent(targetID)}`;
}

export function getCommentDraftTarget(bedID: string, patient: PatientRecord | null | undefined) {
  if (patient?.uuid) return `patient:${patient.uuid}`;
  if (patient) return `patient:${patient.id}:${patient.created_at ?? ''}`;
  return `bed:${bedID}:empty`;
}

export function getCommentDraft(userID: string, targetID: string) {
  if (!userID || !targetID) return '';
  try {
    return draftStorage()?.getItem(draftKey(userID, targetID)) ?? '';
  } catch {
    return '';
  }
}

export function setCommentDraft(userID: string, targetID: string, text: string) {
  if (!userID || !targetID) return;
  const storage = draftStorage();
  if (!storage) return;

  try {
    const key = draftKey(userID, targetID);
    if (text) {
      storage.setItem(key, text);
      return;
    }
    storage.removeItem(key);
  } catch {
    // Draft persistence is best effort; typing must remain available.
  }
}

export function removeCommentDraft(userID: string, targetID: string) {
  if (!userID || !targetID) return;
  try {
    draftStorage()?.removeItem(draftKey(userID, targetID));
  } catch {
    // Storage can be disabled or full.
  }
}

export function clearCommentDrafts(userID: string) {
  if (!userID) return;
  const storage = draftStorage();
  if (!storage) return;

  try {
    const prefix = userPrefix(userID);
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) storage.removeItem(key);
    }
  } catch {
    // Storage can be disabled or unavailable during session teardown.
  }
}
